import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { Accordion, Button, Icon, Modal } from 'semantic-ui-react';
import MyButton from '../common/Button';
import moment from '../../services/moment_instance';
import { chPerm, dayInTZ } from '../helpers';
import { SingleDatePicker } from 'react-dates';

const AttendanceActions = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35em;
  align-items: center;
  vertical-align: middle;
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const ChangeDateSection = styled.div`
  margin: 0 0 20px 0;
`;

const DateFieldLabel = styled.label`
  margin: 0;
`;

const StaffMasterWrap = styled.div`
  margin-bottom: 8px;
`;

const ErrorMessage = styled.p`
  color: red;
`;

const CommentLine = styled.span`
  padding: 0 0 7px 0;
  color: black;
  display: block;
`;

const StyledModal = styled(Modal)`
  &&& {
    background-color: #fff;
  }
`;

const StyledModalHeader = styled(Modal.Header)`
  &&& {
    color: #1a2a33 !important;
    background: #fff !important;
    border-bottom: 1px solid rgba(34, 36, 38, 0.15);
  }
`;

const StyledModalDescription = styled(Modal.Description)`
  &&& {
    color: #1a2a33;
  }
`;

const StyledModalContent = styled(Modal.Content)`
  &&& {
    background: #fff;
    color: #1a2a33;
  }
`;

const StyledModalActions = styled(Modal.Actions)`
  &&& {
    background: #f9fafb;
    border-top: 1px solid rgba(34, 36, 38, 0.15);
  }
`;
export const formatCompletedAttendance = (date, format = 'DD-MM-YYYY, h:mm:ss a') => {
  if (!date) return 'Sin completar';
  let attendanceDateString = `Asistencia: ${dayInTZ(date, format)}`;
  if (dayInTZ(date, 'YYYY-MM-DD') === '2000-01-01') {
    attendanceDateString = 'Marcado por el usuario';
  } else if (dayInTZ(date, 'YYYY-MM-DD') === '2019-12-31') {
    attendanceDateString = '¡Completado!';
  }
  return attendanceDateString;
};

const AttendanceModal = (props) => {
  const {
    title,
    loading,
    event,
    canCreate,
    content,
    updateAttendance,
    set_openModal,
    resetForm,
    loadedData,
    openModal,
    set_changedDate,
    changedDate,
    kind,
    eventDates,
    createAttendance,
    comments,
    checkIfIsAllowedToTake,
  } = props;
  const [activeIndex, set_activeIndex] = React.useState(null);
  const [focused, set_focused] = React.useState(false);
  const [activeChangeDate, set_activeChangeDate] = React.useState(false);
  const [removeAttendanceMode, set_removeAttendanceMode] = React.useState(false);

  const handleClick = (index) => {
    const newIndex = activeIndex === index ? -1 : index;
    set_activeIndex(newIndex);
  };

  useEffect(() => {
    if (!content?.[0]?.percentage) {
      set_activeIndex(0);
    }
  }, [content?.[0]?.percentage]);

  const authToChangeDate =
    (chPerm(['seminaryAdmin']) && kind === 'seminary') ||
    (chPerm(['crecerAdmin']) && kind === 'crecer');

  const removeAttendanceMasterButton = () => (
    <Button
      size="tiny"
      toggle
      active={removeAttendanceMode}
      onClick={() => set_removeAttendanceMode((v) => !v)}
    >
      {removeAttendanceMode ? 'Cancelar quitar asistencias' : 'Quitar asistencias'}
    </Button>
  );

  const removeAttendanceControl = (content, date) => {
    const hasAttendance = Boolean(content?.[0]?.[date.attendanceCode]);
    const canRemove =
      removeAttendanceMode &&
      hasAttendance &&
      (authToChangeDate ||
        (chPerm(['crecer', 'seminary']) && dayInTZ(date.attendanceDate) === dayInTZ(new Date())));
    if (!canRemove) return null;
    return (
      <Button
        compact
        negative
        loading={loading}
        onClick={() =>
          updateAttendance({
            classNumber: date.attendanceCode,
            event,
            remove: true,
          })
        }
      >
        Quitar
      </Button>
    );
  };

  const checkAndRegisterAttendance = (content, date) => {
    const showDate = () => {
      if (content?.[0]?.[date.attendanceCode]) {
        return formatCompletedAttendance(content?.[0]?.[date.attendanceCode]);
      } else {
        return 'Sin completar.';
      }
    };

    const showMarkOrUpdate =
      chPerm(['crecer', 'seminary']) &&
      ((dayInTZ(date.attendanceDate) === dayInTZ(new Date()) &&
        dayInTZ(content?.[0]?.[date.attendanceCode]) !== dayInTZ(new Date())) ||
        (activeChangeDate &&
          authToChangeDate &&
          dayInTZ(content?.[0]?.[date.attendanceCode]) !== dayInTZ(changedDate)));

    return (
      <>
        {showDate()}{' '}
        <AttendanceActions>
          {showMarkOrUpdate && (
            <Button
              compact
              primary
              loading={loading}
              onClick={() =>
                updateAttendance({
                  classNumber: date.attendanceCode,
                  event: event,
                  today: new Date(),
                })
              }
            >
              {`${content?.[0]?.[date.attendanceCode] ? 'Actualizar' : 'Marcar'} asistencia de ${
                date.attendanceName
              }`}
            </Button>
          )}
          {removeAttendanceControl(content, date)}
        </AttendanceActions>
      </>
    );
  };

  const changeDate = () => {
    if (authToChangeDate)
      return (
        <>
          {!activeChangeDate && (
            <ToolbarRow>
              <Button size="tiny" onClick={() => set_activeChangeDate(!activeChangeDate)}>
                Cambiar fecha
              </Button>
              {removeAttendanceMasterButton()}
            </ToolbarRow>
          )}
          {activeChangeDate && (
            <ChangeDateSection>
              <ToolbarRow>
                <DateFieldLabel htmlFor="date">Fecha de asistencia</DateFieldLabel>
                {removeAttendanceMasterButton()}
              </ToolbarRow>
              <SingleDatePicker
                date={
                  new Date(changedDate) === 'Invalid Date' || !changedDate
                    ? null
                    : moment(changedDate)
                }
                onDateChange={(newDate) => set_changedDate(newDate ? newDate._d : newDate)}
                focused={focused}
                onFocusChange={({ focused }) => set_focused(focused)}
                placeholder="dd/mm/aaaa"
                showDefaultInputIcon={true}
                numberOfMonths={1}
                displayFormat="DD/MM/YYYY"
                isOutsideRange={() => false}
                id="date"
              />
            </ChangeDateSection>
          )}
        </>
      );
    return null;
  };

  const removeAttendanceMasterForStaff = () => {
    if (authToChangeDate || !chPerm(['crecer', 'seminary'])) return null;
    return <StaffMasterWrap>{removeAttendanceMasterButton()}</StaffMasterWrap>;
  };

  const attendanceAtChurch = () => {
    return (
      <>
        <Accordion.Title active={activeIndex === 0} index={0} onClick={() => handleClick(0)}>
          <Icon name="dropdown" />
          Presencial
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 0}>
          {removeAttendanceMasterForStaff()}
          {changeDate()}
          {loadedData && <ErrorMessage>{loadedData}</ErrorMessage>}
          {content && content.length === 0 && <p>La persona no tiene asistencia de {title}.</p>}
          {checkIfIsAllowedToTake && !checkIfIsAllowedToTake(event) && (
            <ErrorMessage>
              El asistente no {authToChangeDate ? 'debe' : 'puede'} tomar este curso porque no a
              completado los pasos anteriores.
            </ErrorMessage>
          )}
          {((checkIfIsAllowedToTake && checkIfIsAllowedToTake(event)) || authToChangeDate) && (
            <>
              {content && content.length === 0 && canCreate && (
                <Button
                  loading={loading}
                  primary
                  onClick={() => createAttendance({ event: event })}
                >
                  {'Iniciar asistencia de ' + title}
                </Button>
              )}
              {content?.length !== 0 &&
                eventDates &&
                eventDates
                  ?.filter((d) => d.event === event)
                  ?.map((date) => (
                    <p key={date.attendanceCode}>
                      <strong>{date.attendanceName}:</strong>{' '}
                      {checkAndRegisterAttendance(content, date)}
                    </p>
                  ))}
            </>
          )}
        </Accordion.Content>
      </>
    );
  };

  const virtualAttendance = () => {
    if (content?.[0] && content?.[0]?.percentage) {
      return (
        <>
          <Accordion.Title active={activeIndex === 1} index={1} onClick={() => handleClick(1)}>
            <Icon name="dropdown" />
            Online
          </Accordion.Title>
          <Accordion.Content active={activeIndex === 1}>
            <p>
              <strong>Porcentaje completado:</strong> {content[0].percentage}%
              <br />
              <small>
                <i>Este valor se actualiza de manera periodica.</i>
              </small>
            </p>
          </Accordion.Content>
        </>
      );
    }
  };

  return (
    <StyledModal
      trigger={<MyButton text={title} onClick={() => set_openModal(event)} />}
      open={openModal === event}
      onClose={() => {
        set_removeAttendanceMode(false);
        set_openModal('');
      }}
      size="small"
    >
      <StyledModalHeader>{title}</StyledModalHeader>
      <StyledModalContent scrolling>
        {comments &&
          comments?.map((comment, idx) => (
            <CommentLine key={idx}>
              <i>{comment}</i>
            </CommentLine>
          ))}
        <StyledModalDescription>
          <Accordion styled>
            {virtualAttendance()}
            {attendanceAtChurch()}
          </Accordion>
        </StyledModalDescription>
      </StyledModalContent>
      <StyledModalActions>
        <Button secundary onClick={() => set_openModal('')}>
          Volver
        </Button>
        {canCreate && (
          <Button
            primary
            onClick={() => {
              resetForm();
              set_openModal('');
            }}
          >
            Ingresar otro asistente
          </Button>
        )}
      </StyledModalActions>
    </StyledModal>
  );
};

export default AttendanceModal;
